import React, { useRef, useState } from 'react'
import styled from 'styled-components'
import { Swiper, SwiperSlide } from 'swiper/react'

import { media } from 'styles/media'

import ChevronDownIcon from '../../icons/ChevronDown'

import ValueCard from './ValueCard'
import values from './values'
import type { PositionProps } from './types'

const Arrow = styled.button.attrs({ type: 'button' })<{
  left?: boolean
  right?: boolean
  disabled?: boolean
}>`
  height: 48px;
  width: 48px;
  padding: 0;

  ${({ left }) =>
    left &&
    `
    left: 16px;

    svg {
      margin-left: -2px;
    }
  `}

  ${({ right }) =>
    right &&
    `
    right: 16px;
    svg {
      margin-right: -2px;
    }
  `}

  appearance: none;
  outline: 0;
  border: 1px solid ${({ theme }) => theme?.['-ds-neutral-500']?.value};
  border-radius: 50%;
  color: ${({ theme }) => theme?.['-ds-neutral-600']?.value};
  background-color: white;
  cursor: pointer;

  svg {
    display: inline-block;
    vertical-align: middle;
    height: 14px;
    margin-top: -2px;
    transform: rotate(${({ left }) => (left ? '90deg' : '-90deg')});
  }

  ${({ disabled }) =>
    disabled &&
    `
    color: #999;
  `}

  &:not(:last-child) {
    margin-right: 16px;
  }
`

const ArrowsWrapper = styled.div<PositionProps>`
  position: absolute;
  right: 32px;

  ${media.xxl`
    right: calc((100vw - 1300px) / 2);
  `}

  ${({ desktopMarginTop }) =>
    desktopMarginTop
      ? `
    top: calc(-50px - ${desktopMarginTop}px);
  `
      : `
    top: -50px;
  `}
`

const Outer = styled.div<PositionProps>`
  width: 100%;

  .swiper-container {
    height: 273px;
    ${({ desktopMarginTop }) =>
      desktopMarginTop &&
      `
      margin-top: ${desktopMarginTop}px;
    `};
    overflow-y: hidden;
  }

  .swiper-slide {
    width: auto;

    &:first-child {
      margin-left: 32px;

      ${media.xxl`
        margin-left: calc((100vw - 1300px) / 2);
      `}
    }

    &:last-child {
      margin-right: 32px;
    }
  }

  position: relative;
`

const Slider = ({ desktopMarginTop = 56 }: PositionProps) => {
  const [swiper, setSwiper] = useState(null)

  const goNext = () => swiper && swiper.slideNext()
  const goPrev = () => swiper && swiper.slidePrev()

  const nextRef = useRef<HTMLButtonElement>()
  const prevRef = useRef<HTMLButtonElement>()

  return (
    <Outer desktopMarginTop={desktopMarginTop}>
      <ArrowsWrapper desktopMarginTop={desktopMarginTop}>
        <Arrow ref={prevRef} onClick={goPrev} left aria-label="slides back">
          <ChevronDownIcon />
        </Arrow>
        <Arrow ref={nextRef} onClick={goNext} right aria-label="slides forward">
          <ChevronDownIcon />
        </Arrow>
      </ArrowsWrapper>

      <Swiper onSwiper={setSwiper} slidesPerView="auto" spaceBetween={32}>
        {values.map(({ text, Icon, backgroundColor }) => (
          <SwiperSlide key={text}>
            <ValueCard
              icon={<Icon />}
              text={text}
              backgroundColor={backgroundColor}
            />
          </SwiperSlide>
        ))}
      </Swiper>
    </Outer>
  )
}

export default Slider
